import {
  InvoiceQueryReason,
  InvoiceQueryReasonLabels,
  IPortalInvoiceResponse,
  REQUIRES_VERIFY_MESSAGE,
} from "shared";
import { useState } from "react";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

import { Button } from "../../../common/Atoms/Button";
import { Modal } from "../../../common/Atoms/Modal";
import { Select } from "../../../common/Atoms/Select";
import { TextArea } from "../../../common/Atoms/TextArea";
import { useCreateInvoiceQueryMutation } from "../../services/api/invoice";
import { useSession } from "../../hooks/useSession";

import { InvoiceOption } from "./InvoiceOption";

const DETAIL_MAX_LENGTH = 2000;

const reasonOptions = Object.values(InvoiceQueryReason).map(value => ({
  label: InvoiceQueryReasonLabels[value],
  value,
}));

interface IQueryInvoiceProps {
  invoiceData: IPortalInvoiceResponse;
  onRequireVerification: () => void;
}

export function canQueryInvoice(invoiceData: IPortalInvoiceResponse) {
  return !!invoiceData.queriesEnabled && invoiceData.invoice?.xero_status === `AUTHORISED` && (invoiceData.invoice.xero_amount_due ?? 0) > 0;
}

export function QueryInvoice({ invoiceData, onRequireVerification }: IQueryInvoiceProps) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<InvoiceQueryReason | null>(null);
  const [detail, setDetail] = useState<string>(``);
  const [createQuery, { isLoading }] = useCreateInvoiceQueryMutation();

  if (!canQueryInvoice(invoiceData)) {
    return null;
  }

  const current = invoiceData.currentQuery;
  const trimmedDetail = detail.trim();
  const detailMissing = reason === InvoiceQueryReason.OTHER && !trimmedDetail;
  const detailTooLong = detail.length > DETAIL_MAX_LENGTH;

  async function onSubmit() {
    if (!session?.id || !reason) return;

    try {
      await createQuery({
        code: invoiceData.invoice.hash,
        sessionId: session.id,
        reason,
        detail: trimmedDetail || null,
      }).unwrap();

      toast.success(`Thanks, we have received your query`);
      setOpen(false);
      setReason(null);
      setDetail(``);
    }
    catch (err) {
      const status = err?.status;
      const message = err?.data?.message || err?.data;

      if (status === 401 || message === REQUIRES_VERIFY_MESSAGE) {
        setOpen(false);
        onRequireVerification();

        return;
      }

      toast.error(typeof message === `string` ? message : `Could not send your query`);
    }
  }

  if (current) {
    return (
      <InvoiceOption
        icon={ QuestionMarkCircleIcon }
        tone={ `orange` }
        title={ `Query raised` }
        description={ `You raised a query about this invoice on ${DateTime.fromJSDate(new Date(current.createdAt)).toFormat(`ccc dd LLL yyyy`)}. We will be in touch.` }
      />
    );
  }

  return (
    <>
      <InvoiceOption
        icon={ QuestionMarkCircleIcon }
        title={ `Query this invoice` }
        description={ `Something not right with this invoice? Let us know and we will look into it.` }
        onClick={ () => setOpen(true) }
      />

      <Modal
        open={ open }
        setOpen={ setOpen }
        title={ `Query this invoice` }
      >
        <div className={ `space-y-3` }>
          <Select
            label={ `What is the problem?` }
            name={ `reason` }
            options={ reasonOptions }
            selected={ reason }
            onChange={ ({ value }) => setReason(value) }
          />
          <TextArea
            label={ reason === InvoiceQueryReason.OTHER ? `Tell us more (required)` : `Tell us more` }
            name={ `detail` }
            value={ detail }
            rows={ 4 }
            onChange={ ({ value }) => setDetail(String(value)) }
            description={ detailTooLong ? `Please keep this under ${DETAIL_MAX_LENGTH} characters` : undefined }
          />
          <div className={ `flex justify-end gap-2` }>
            <Button color={ `none` }
              onClick={ () => setOpen(false) }
              disabled={ isLoading }>
              { `Cancel` }
            </Button>
            <Button onClick={ onSubmit }
              loading={ isLoading }
              disabled={ !reason || detailMissing || detailTooLong || isLoading }>
              { `Send query` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
